import React, { useState, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import { AdminHeader } from './admin/AdminHeader';
import { LeadsList, TemplateList } from './admin/SelectionLists';
import { ComposeMessage } from './admin/ComposeMessage';
import { SmsHistory } from './admin/SmsHistory';
import { useSmsData, fillTemplate } from './admin/useSmsData';

const EMPTY_DRAFT = { name: '', phone: '', message: '' };

const AdminSMS = () => {
  const { toast } = useToast();
  const onError = useCallback((description) => toast({ title: 'Error', description, variant: 'destructive' }), [toast]);
  const { templates, twilioConfigured, leads, smsLogs, fetchSmsLogs, sendSms } = useSmsData(onError);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sending, setSending] = useState(false);

  const updateDraft = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const selectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const message = fillTemplate(templates[templateId].template, { name: draft.name, service: selectedLead && (selectedLead.service || 'your service') });
    updateDraft('message', message);
  };

  const selectLead = (lead) => {
    setSelectedLead(lead);
    setDraft((prev) => ({
      name: lead.name,
      phone: lead.phone,
      message: selectedTemplate ? fillTemplate(templates[selectedTemplate].template, { name: lead.name, service: lead.service || 'your service' }) : prev.message,
    }));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await sendSms({ phone: draft.phone, message: draft.message, customer_name: draft.name, template_id: selectedTemplate });
      toast({
        title: data.demo_mode ? 'SMS Logged (Demo Mode)' : 'SMS Sent!',
        description: data.demo_mode ? 'Twilio not configured. SMS logged for testing.' : `Message sent to ${draft.phone}`,
      });
      fetchSmsLogs();
      updateDraft('message', '');
      setSelectedTemplate(null);
    } catch (error) {
      onError(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(draft.message);
    toast({ title: 'Copied!', description: 'Message copied to clipboard' });
  };

  return (
    <div className="min-h-screen bg-black">
      <AdminHeader twilioConfigured={twilioConfigured} />
      <div className="pt-24 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">SMS Customer Outreach</h1>
            <p className="text-gray-400">Send professional messages to leads and customers</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="space-y-6">
              <LeadsList leads={leads} selectedId={selectedLead?.id} onSelect={selectLead} />
              <TemplateList templates={templates} selectedId={selectedTemplate} onSelect={selectTemplate} />
            </div>
            <div className="lg:col-span-2">
              <ComposeMessage {...draft} onChange={updateDraft} onSend={handleSend} onCopy={copyMessage} sending={sending} twilioConfigured={twilioConfigured} />
              <SmsHistory logs={smsLogs} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSMS;
